import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UtilService from "../../../services/UtilService";
import Item from "../../components/Item";
import KeywordBox from "../../components/KeywordBox";
import CustomButton from "../../components/CustomButton";
import ConfirmDialog from "../../components/ConfirmDialog";
import LeaderBoardService from "./LeaderBoardService";
import Logger from "../../../services/Logger";

const STORAGE_KEY = "admin.leaderBoard.searchOptions";
const PAGE_SIZE_OPTIONS = new Set([15, 25, 50, 100]);
const DEFAULT_SORT = [{ field: "score", sort: "desc" }];
const PUZZLE_TYPE_VALUES = new Set([
    "",
    "CROSSWORD",
    "WORD_SEARCH",
    "HIDDEN_MESSAGE",
    "WORD_WHEEL",
    "SLIDING",
    "JIGSAW",
]);

const PERIOD_OPTIONS = [
    { value: "today", label: "Today" },
    { value: "week", label: "This week" },
    { value: "all-time", label: "All time" },
];

const TEST_FILTER_OPTIONS = [
    { value: "all", label: "List all" },
    { value: "testers", label: "Tester only" },
    { value: "exclude", label: "Exclude testers" },
];

const PUZZLE_TYPE_OPTIONS = [
    { value: "", label: "Overall" },
    { value: "CROSSWORD", label: "Crossword" },
    { value: "WORD_SEARCH", label: "Word Search" },
    { value: "HIDDEN_MESSAGE", label: "Hidden Message" },
    { value: "WORD_WHEEL", label: "Word Wheel" },
    { value: "SLIDING", label: "Sliding" },
    { value: "JIGSAW", label: "Jigsaw" },
];

function defaultSearchOptions() {
    return {
        keyword: "",
        puzzleType: "",
        period: "all-time",
        testFilter: "all",
        page: 0,
        pageSize: 25,
        sort: DEFAULT_SORT,
    };
}

function loadSearchOptions() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return defaultSearchOptions();
        }
        const parsed = JSON.parse(raw);
        const pageSize = PAGE_SIZE_OPTIONS.has(parsed?.pageSize) ? parsed.pageSize : 25;
        const page = Number.isFinite(parsed?.page) && parsed.page >= 0 ? parsed.page : 0;
        const sort = Array.isArray(parsed?.sort) && parsed.sort.length > 0
            ? parsed.sort
            : DEFAULT_SORT;
        return {
            keyword: typeof parsed.keyword === "string" ? parsed.keyword : "",
            puzzleType: PUZZLE_TYPE_VALUES.has(parsed?.puzzleType) ? parsed.puzzleType : "",
            period: PERIOD_OPTIONS.some((opt) => opt.value === parsed?.period) ? parsed.period : "all-time",
            testFilter: TEST_FILTER_OPTIONS.some((opt) => opt.value === parsed?.testFilter)
                ? parsed.testFilter
                : parsed?.includeTest === false
                    ? "exclude"
                    : "all",
            page,
            pageSize,
            sort,
        };
    } catch {
        return defaultSearchOptions();
    }
}

function saveSearchOptions(options) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
    } catch {
        /* ignore quota / private mode */
    }
}

function formatEpochMs(value) {
    if (value == null || value === 0) return "—";
    return UtilService.formatMDY(value) || "—";
}

function puzzleTypeLabel(type) {
    const match = PUZZLE_TYPE_OPTIONS.find((o) => o.value === type);
    return match?.label || type || "Overall";
}

export default function LeaderBoardMain() {
    const initialSearch = useMemo(() => loadSearchOptions(), []);
    const [keyword, setKeyword] = useState(initialSearch.keyword);
    const [puzzleType, setPuzzleType] = useState(initialSearch.puzzleType);
    const [period, setPeriod] = useState(initialSearch.period);
    const [testFilter, setTestFilter] = useState(initialSearch.testFilter);
    const [pageModel, setPageModel] = useState({
        page: initialSearch.page,
        pageSize: initialSearch.pageSize,
    });
    const [sort, setSort] = useState(initialSearch.sort);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [addingScoreId, setAddingScoreId] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [syncOpen, setSyncOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [addTestOpen, setAddTestOpen] = useState(false);
    const [addingTest, setAddingTest] = useState(false);
    const [testName, setTestName] = useState("");
    const [testScore, setTestScore] = useState("");
    const [testPuzzleType, setTestPuzzleType] = useState("");
    const [error, setError] = useState(null);
    const [syncMessage, setSyncMessage] = useState(null);

    useEffect(() => {
        saveSearchOptions({
            keyword,
            puzzleType,
            period,
            testFilter,
            page: pageModel.page,
            pageSize: pageModel.pageSize,
            sort,
        });
    }, [keyword, puzzleType, period, testFilter, pageModel, sort]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await LeaderBoardService.listStandings(pageModel, sort, {
                keyword,
                puzzleType,
                testFilter,
                period,
            });
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || (result.error && !Array.isArray(result.content))
            ) {
                const msg =
                    result?.message
                    || result?.error
                    || "Failed to load leader board";
                setError(msg);
                setRows([]);
                setRowCount(0);
                Logger.warn("leader-board load failed:", result);
                return;
            }
                            const content = Array.isArray(result?.content) ? result.content : [];
            setRows(
                content.map((row, index) => {
                    return {
                        id: row.id || `row-${index}`,
                        rank: row.rank,
                        alias: row.alias || "",
                        nickname: Boolean(row.testAccount)
                            ? "Anonymous"
                            : (row.nickname || "Anonymous"),
                        isTest: Boolean(row.testAccount),
                        canAddRandomScore: Boolean(
                            row.canAddRandomScore
                            || row.testAccount
                            || String(row.cloudUser?.email || "").toLowerCase() === "support@puzzleinteract.com"
                        ),
                        puzzleType: row.puzzleType || null,
                        score: row.score ?? 0,
                        playerScoreAt: row.playerScoreAt,
                        adminUpdatedAt: row.adminUpdatedAt,
                    };
                })
            );
            setRowCount(LeaderBoardService.totalElements(result));
        } catch (err) {
            setRows([]);
            setRowCount(0);
            setError(err?.message || "Failed to load leader board");
            Logger.error("leader-board load:", err);
        } finally {
            setLoading(false);
        }
    }, [keyword, puzzleType, period, testFilter, pageModel, sort]);

    useEffect(() => {
        load();
    }, [load]);

    const handleAddRandomScore = useCallback(async (standingId) => {
        if (!standingId) return;
        setAddingScoreId(standingId);
        setError(null);
        try {
            const result = await LeaderBoardService.addRandomScore(standingId);
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to add random score");
                return;
            }
            await load();
        } catch (err) {
            setError(err?.message || "Failed to add random score");
            Logger.error("leader-board add random score:", err);
        } finally {
            setAddingScoreId(null);
        }
    }, [load]);

    const handleConfirmRemove = useCallback(async (standingId) => {
        if (!standingId) return;
        setRemovingId(standingId);
        setError(null);
        try {
            const result = await LeaderBoardService.removeStanding(standingId);
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to remove standing");
                return;
            }
            await load();
        } catch (err) {
            setError(err?.message || "Failed to remove standing");
            Logger.error("leader-board remove:", err);
        } finally {
            setRemovingId(null);
            setRemoveTarget(null);
        }
    }, [load]);

    const canSyncFromPlays = puzzleType === "JIGSAW" || puzzleType === "SLIDING" || puzzleType === "WORD_WHEEL";

    const openAddTest = useCallback(() => {
        setTestName("");
        setTestScore("");
        setTestPuzzleType("");
        setAddTestOpen(true);
        setError(null);
    }, []);

    const handleAddTestAccount = useCallback(async () => {
        const name = testName.trim();
        const scoreText = testScore.trim();
        if (!name || scoreText === "" || !testPuzzleType || addingTest) return;
        const score = Number(scoreText);
        if (!Number.isInteger(score) || score < 0) {
            setError("Score must be a whole number, 0 or greater");
            return;
        }
        setAddingTest(true);
        setError(null);
        try {
            const result = await LeaderBoardService.addTestAccount(name, testPuzzleType, score);
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to add test account");
                return;
            }
            setAddTestOpen(false);
            const alreadyVisible = puzzleType === testPuzzleType && testFilter !== "exclude" && pageModel.page === 0;
            if (testFilter === "exclude") {
                setTestFilter("all");
            }
            setPuzzleType(testPuzzleType);
            setPageModel((prev) => ({ ...prev, page: 0 }));
            if (alreadyVisible) {
                await load();
            }
        } catch (err) {
            setError(err?.message || "Failed to add test account");
            Logger.error("leader-board add test account:", err);
        } finally {
            setAddingTest(false);
        }
    }, [addingTest, load, pageModel.page, puzzleType, testFilter, testName, testPuzzleType, testScore]);

    const syncTypeLabel = puzzleTypeLabel(puzzleType);
    const syncDescription = puzzleType === "WORD_WHEEL"
        ? [
            `Only ${syncTypeLabel} is rebuilt. Other puzzle types stay as they are.`,
            "Each Word Wheel puzzle is counted once. A finished puzzle counts every word in it. An unfinished puzzle counts the words found so far.",
            "After this, each new Word Wheel word adds 1 to that player’s stored score. Opening this page does not recalculate.",
        ]
        : puzzleType === "SLIDING"
            ? [
                `Only ${syncTypeLabel} is rebuilt. Other puzzle types stay as they are.`,
                "Each sliding puzzle is counted once. A finished puzzle counts as 1. A board that is already solved counts even when the completed flag was not saved. An unfinished puzzle counts as 0.",
                "Signed-in players and guest sessions are both included. After this, each new completion adds 1. Opening this page does not recalculate.",
            ]
            : [
                `Only ${syncTypeLabel} is rebuilt. Other puzzle types stay as they are.`,
                `Each score becomes the number of ${syncTypeLabel} puzzles that player has completed.`,
                "After this, each new completion adds 1. Opening this page does not recalculate.",
            ];

    const handleSyncFromPlays = useCallback(async () => {
        if (!canSyncFromPlays || syncing) return;
        setSyncing(true);
        setError(null);
        setSyncMessage(null);
        try {
            const result = await LeaderBoardService.syncFromPlays(puzzleType);
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to sync scores from plays");
                return;
            }
            const upserted = result.upserted ?? result.data?.upserted ?? 0;
            const unchanged = result.unchanged ?? result.data?.unchanged ?? 0;
            const guestUpserted = result.guestUpserted ?? result.data?.guestUpserted ?? 0;
            const guestUnchanged = result.guestUnchanged ?? result.data?.guestUnchanged ?? 0;
            setSyncMessage(
                `Synced ${puzzleType}: ${upserted} users updated (${unchanged} ok), `
                + `${guestUpserted} guests updated (${guestUnchanged} ok).`,
            );
            await load();
        } catch (err) {
            setError(err?.message || "Failed to sync scores from plays");
            Logger.error("leader-board sync from plays:", err);
        } finally {
            setSyncing(false);
        }
    }, [canSyncFromPlays, load, puzzleType, syncing]);

    const columns = useMemo(
        () => [
            {
                field: "rank",
                headerName: "Rank",
                flex: 0.5,
                minWidth: 64,
                sortable: false,
            },
            {
                field: "alias",
                headerName: "Alias",
                flex: 1,
                minWidth: 140,
                sortable: false,
                valueFormatter: (value) => value || "—",
            },
            {
                field: "nickname",
                headerName: "Nickname",
                flex: 1,
                minWidth: 120,
                sortable: false,
                valueFormatter: (value) => value || "Anonymous",
            },
            {
                field: "isTest",
                headerName: "Is test",
                flex: 0.6,
                minWidth: 80,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" color="warning" label="Yes" />
                    ) : (
                        <Chip size="small" variant="outlined" label="No" />
                    ),
            },
            {
                field: "puzzleType",
                headerName: "Puzzle",
                flex: 0.8,
                minWidth: 110,
                sortable: false,
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" label={puzzleTypeLabel(params.value)} />
                    ) : (
                        <Chip size="small" color="primary" label="Overall" />
                    ),
            },
            {
                field: "score",
                headerName: "Score",
                flex: 0.5,
                minWidth: 72,
            },
            {
                field: "playerScoreAt",
                headerName: "User updated",
                flex: 0.7,
                minWidth: 110,
                sortable: false,
                valueFormatter: (value) => formatEpochMs(value),
            },
            {
                field: "adminUpdatedAt",
                headerName: "We updated",
                flex: 0.7,
                minWidth: 110,
                sortable: false,
                valueFormatter: (value) => formatEpochMs(value),
            },
            {
                field: "actions",
                headerName: "Actions",
                flex: 1.4,
                minWidth: 260,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                display: "flex",
                renderCell: (params) => {
                    const canAdd = Boolean(params.row?.canAddRandomScore || params.row?.isTest);
                    if (!canAdd && !params.row?.isTest) {
                        return null;
                    }
                    const addBusy = addingScoreId === params.row.id;
                    const removeBusy = removingId === params.row.id;
                    const busy = addBusy || removeBusy || loading;
                    return (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: "100%", height: "100%" }}
                        >
                            {canAdd ? (
                                <CustomButton
                                    size="small"
                                    variant="outlined"
                                    disabled={busy}
                                    onClick={() => handleAddRandomScore(params.row.id)}
                                >
                                    {addBusy ? "Adding…" : "Add random score"}
                                </CustomButton>
                            ) : null}
                            {params.row?.isTest ? (
                                <CustomButton
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={busy}
                                    onClick={() => setRemoveTarget({
                                        id: params.row.id,
                                        alias: params.row.alias,
                                    })}
                                >
                                    {removeBusy ? "Removing…" : "Remove"}
                                </CustomButton>
                            ) : null}
                        </Stack>
                    );
                },
            },
        ],
        [addingScoreId, handleAddRandomScore, loading, removingId]
    );

    return (
        <Box sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 2,
                                alignItems: "center",
                            }}
                        >
                            <KeywordBox
                                callback={(value) => {
                                    setKeyword(value);
                                    setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                                }}
                                initialValue={initialSearch.keyword}
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="lb-puzzle-type-label">Puzzle type</InputLabel>
                                <Select
                                    labelId="lb-puzzle-type-label"
                                    label="Puzzle type"
                                    value={puzzleType}
                                    onChange={(e) => {
                                        setPuzzleType(e.target.value);
                                        setPageModel((prev) => ({ ...prev, page: 0 }));
                                    }}
                                >
                                    {PUZZLE_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value || "overall"} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel id="lb-period-label">Period</InputLabel>
                                <Select
                                    labelId="lb-period-label"
                                    label="Period"
                                    value={period}
                                    onChange={(e) => {
                                        setPeriod(e.target.value);
                                        setPageModel((prev) => ({ ...prev, page: 0 }));
                                    }}
                                >
                                    {PERIOD_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel id="lb-test-filter-label">Testers</InputLabel>
                                <Select
                                    labelId="lb-test-filter-label"
                                    label="Testers"
                                    value={testFilter}
                                    onChange={(e) => {
                                        setTestFilter(e.target.value);
                                        setPageModel((prev) => ({ ...prev, page: 0 }));
                                    }}
                                >
                                    {TEST_FILTER_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <CustomButton
                                size="small"
                                variant="outlined"
                                disabled={addingTest || loading}
                                onClick={openAddTest}
                            >
                                Add tester
                            </CustomButton>
                            {canSyncFromPlays ? (
                                <CustomButton
                                    size="small"
                                    variant="outlined"
                                    disabled={syncing || loading}
                                    onClick={() => setSyncOpen(true)}
                                >
                                    {syncing ? "Syncing…" : "Sync scores from plays"}
                                </CustomButton>
                            ) : null}
                            <Typography variant="body2" color="text.secondary">
                                Choose Word Wheel, jigsaw, or sliding, then open Sync scores from plays.
                                The popup explains the rebuild before it runs.
                            </Typography>
                        </Box>
                        {error ? (
                            <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                                {error}
                            </Typography>
                        ) : null}
                        {syncMessage ? (
                            <Typography color="success.main" variant="body2" sx={{ mt: 1.5 }}>
                                {syncMessage}
                            </Typography>
                        ) : null}
                        {!loading && !error && rowCount === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                No standings yet. Choose Word Wheel and use Sync scores from plays to fill
                                scores from words already found. New words add 1 after that. Jigsaw and
                                sliding sync from completed puzzles.
                                Alias-only test rows do not need a user account. Use List all or
                                Tester only to see them.
                            </Typography>
                        ) : null}
                    </Item>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
                    <Item sx={{ minWidth: 0, overflow: "hidden" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            rowCount={rowCount}
                            loading={loading}
                            paginationModel={pageModel}
                            onPaginationModelChange={setPageModel}
                            pageSizeOptions={[15, 25, 50, 100]}
                            paginationMode="server"
                            sortingMode="server"
                            sortModel={sort}
                            onSortModelChange={(model) => {
                                setSort(
                                    model.length
                                        ? model
                                        : [{ field: "score", sort: "desc" }]
                                );
                            }}
                            disableRowSelectionOnClick
                            autoHeight
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                border: 0,
                            }}
                        />
                    </Item>
                </Grid>
            </Grid>
            <ConfirmDialog
                open={syncOpen}
                setOpen={setSyncOpen}
                title={`Sync ${syncTypeLabel} scores`}
                targetName={syncTypeLabel}
                message={syncDescription}
                okText="Sync scores"
                cancelText="Cancel"
                callback={handleSyncFromPlays}
            />
            <Dialog
                open={addTestOpen}
                onClose={() => {
                    if (!addingTest) setAddTestOpen(false);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Add tester</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="lb-add-tester-app-label">Application</InputLabel>
                            <Select
                                labelId="lb-add-tester-app-label"
                                label="Application"
                                value={testPuzzleType}
                                onChange={(e) => setTestPuzzleType(e.target.value)}
                            >
                                {PUZZLE_TYPE_OPTIONS.filter((opt) => opt.value).map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            autoFocus
                            label="Name"
                            value={testName}
                            onChange={(e) => setTestName(e.target.value)}
                            inputProps={{ maxLength: 20 }}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="Score"
                            value={testScore}
                            onChange={(e) => setTestScore(e.target.value.replace(/[^\d]/g, ""))}
                            inputProps={{ inputMode: "numeric", maxLength: 9 }}
                            size="small"
                            fullWidth
                            placeholder="0"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <CustomButton
                        onClick={() => setAddTestOpen(false)}
                        disabled={addingTest}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="contained"
                        disabled={addingTest || !testPuzzleType || !testName.trim() || testScore.trim() === ""}
                        onClick={handleAddTestAccount}
                    >
                        {addingTest ? "Adding…" : "Add"}
                    </CustomButton>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={Boolean(removeTarget)}
                setOpen={(open) => {
                    if (!removingId) {
                        if (!open) setRemoveTarget(null);
                    }
                }}
                title="Remove test standing?"
                message={
                    puzzleType
                        ? "This removes this test row from the leader board. It does not delete the user account."
                        : "This removes every puzzle-type standing for this test user. It does not delete the user account."
                }
                targetName={removeTarget?.alias && removeTarget.alias !== "Anonymous" ? removeTarget.alias : ""}
                targetId={removeTarget?.id}
                warningAlert="This cannot be undone from this page."
                callback={handleConfirmRemove}
            />
        </Box>
    );
}
